import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { 
  People, 
  CheckCircle, 
  Schedule, 
  TrendingUp 
} from '@mui/icons-material';

interface CandidateStatsProps {
  candidates: any[];
}

const CandidateStats: React.FC<CandidateStatsProps> = ({ candidates }) => {
  // Calculate statistics
  const totalCandidates = candidates.length;
  const withPIData = candidates.filter(c => 
    c.pi_factor_a && c.pi_factor_b && c.pi_factor_c && c.pi_factor_d
  ).length;
  
  const candidatesWithScores = candidates.filter(c => 
    c.position_match_score !== null && 
    c.position_match_score !== undefined &&
    !isNaN(parseFloat(c.position_match_score))
  );
  
  const avgMatchScore = candidatesWithScores.length > 0
    ? candidatesWithScores.reduce((sum, c) => sum + parseFloat(c.position_match_score), 0) / candidatesWithScores.length
    : 0;
  
  const statusCounts = candidates.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const stats = [
    {
      label: 'Total Candidates',
      value: totalCandidates,
      icon: <People />,
      color: 'primary.main',
    },
    {
      label: 'With PI Data',
      value: `${withPIData}/${totalCandidates}`,
      icon: <CheckCircle />,
      color: 'success.main',
    },
    {
      label: 'Avg Match Score',
      value: candidatesWithScores.length > 0 && !isNaN(avgMatchScore) 
        ? `${Math.round(avgMatchScore * 10)}%` 
        : 'N/A',
      icon: <TrendingUp />,
      color: 'warning.main',
    },
    {
      label: 'In Progress',
      value: (statusCounts['screening'] || 0) + (statusCounts['interviewing'] || 0),
      icon: <Schedule />,
      color: 'info.main',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(4, 1fr)',
        },
        gap: 2,
        mb: 3,
      }}
    >
      {stats.map((stat, index) => (
        <Paper
          key={index}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              color: stat.color,
              mb: 1,
              '& svg': { fontSize: 32 },
            }}
          >
            {stat.icon}
          </Box>
          <Typography variant="h6" fontWeight="bold">
            {stat.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {stat.label}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default CandidateStats;